/**
 * Client-side sanitizer for the Mixpanel module config persisted via
 * updateWorkspaceModule (modules.config).
 *
 * The module config is read back verbatim by AppAnalyticsPanel and returned
 * unsanitized by GET /modules (mapModuleEntity), so the raw Mixpanel Service
 * Account credentials (serviceAccountUser / serviceAccountSecret) must NEVER be
 * stored here — otherwise they round-trip in plaintext to every client that
 * fetches modules. This mirrors the server-side `sanitizePluginConfig` in
 * apps/api/src/features/agent/plugins.router.ts: drop the raw secrets and the
 * transient `removeServiceAccount` signal, and keep only a boolean
 * `hasServiceAccount` flag (which the panel uses to gate advanced-query UI).
 *
 * The actual SA secrets used by the server-side Mixpanel segmentation proxy
 * live in the plugin_instances store, not in modules.config, so stripping them
 * here does not regress the advanced-query flow.
 */
export function sanitizeMixpanelModuleConfig(
  existing: Record<string, unknown> | undefined,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const blocked = new Set([
    'serviceAccountUser',
    'serviceAccountSecret',
    'removeServiceAccount',
    'hasServiceAccount',
  ]);

  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(incoming)) {
    if (blocked.has(key) || value === undefined) continue;
    next[key] = value;
  }

  const removeServiceAccount = Boolean(incoming['removeServiceAccount']);
  const incomingUser =
    typeof incoming['serviceAccountUser'] === 'string'
      ? (incoming['serviceAccountUser'] as string).trim()
      : '';
  const incomingSecret =
    typeof incoming['serviceAccountSecret'] === 'string'
      ? (incoming['serviceAccountSecret'] as string).trim()
      : '';

  // Resolve the flag: an explicit remove clears it; new valid credentials set
  // it; otherwise preserve whatever the existing module config already had.
  const previouslyHad = Boolean(existing?.['hasServiceAccount']);
  let hasServiceAccount = previouslyHad;
  if (removeServiceAccount) {
    hasServiceAccount = false;
  } else if (incomingUser && incomingSecret) {
    hasServiceAccount = true;
  }

  if (hasServiceAccount) {
    next.hasServiceAccount = true;
  }

  return next;
}
